<template>
  <div class="hms-page">
    <Navigation />
    <div class="container hms-page-body">
      <div class="d-flex align-items-center justify-content-between mb-4 hms-page-header">
        <div class="d-flex align-items-center gap-3">
          <a href="javascript:void(0)" @click="$router.push('/dashboard')" class="hms-back" title="Back"><i class="mdi mdi-arrow-left"></i></a>
          <h4 class="hms-page-title m-0"><i class="mdi mdi-handshake-outline me-2"></i>{{ $t('page.insurance_rates','Insurance Rate Cards') }}</h4>
        </div>
        <button class="hms-btn hms-btn-primary" @click="openNewCard"><i class="mdi mdi-plus me-1"></i>New rate card</button>
      </div>

      <div class="hms-info-banner mb-3">
        <i class="mdi mdi-information-outline me-1"></i>
        Each insurance provider negotiates discounted rates with your facility. Configure a rate card per provider with effective dates;
        either set a <strong>blanket discount %</strong> (applied to every service in the catalogue) or override individual services with
        a <strong>negotiated price</strong>. When billing a patient, the system auto-applies the correct rate based on their insurance.
      </div>

      <div v-if="!cards.length" class="hms-muted text-center py-5">No rate cards yet. Click <strong>New rate card</strong> to start.</div>

      <table v-else class="hms-table">
        <thead>
          <tr><th>Provider</th><th>Card name</th><th>Effective</th><th>Default discount</th><th>Items</th><th>Status</th><th></th></tr>
        </thead>
        <tbody>
          <tr v-for="c in cards" :key="c.card_id" :class="{ 'hms-muted': !c.is_active }">
            <td><strong>{{ c.provider_name }}</strong></td>
            <td>{{ c.card_name || '—' }}</td>
            <td>
              {{ c.effective_from }}
              <span v-if="c.effective_to"> → {{ c.effective_to }}</span>
              <span v-else class="hms-muted-sm"> (ongoing)</span>
            </td>
            <td>{{ c.default_discount_pct !== null ? c.default_discount_pct + '%' : '—' }}</td>
            <td>{{ c.rate_count }}</td>
            <td><span class="hms-badge" :class="c.is_active ? 'hms-badge-success' : 'hms-badge-neutral'">{{ c.is_active ? 'Active' : 'Inactive' }}</span></td>
            <td>
              <button class="hms-btn-icon" @click="openItems(c)" title="Manage rates"><i class="mdi mdi-format-list-bulleted"></i></button>
              <button class="hms-btn-icon" @click="openEditCard(c)" title="Edit"><i class="mdi mdi-pencil"></i></button>
              <button class="hms-btn-icon" @click="onDeleteCard(c)" title="Delete"><i class="mdi mdi-trash-can-outline"></i></button>
            </td>
          </tr>
        </tbody>
      </table>

      <!-- Card form modal -->
      <div v-if="showCardForm" class="hms-modal-overlay" @click.self="showCardForm = false">
        <div class="hms-modal" style="max-width:640px; width:95%;">
          <div class="hms-modal-title">
            <span>{{ cardForm.card_id ? 'Edit rate card' : 'New rate card' }}</span>
            <button class="hms-modal-close" @click="showCardForm = false">&times;</button>
          </div>
          <div class="row g-3">
            <div class="col-md-6"><label class="hms-label">Provider *</label>
              <input v-model="cardForm.provider_name" class="hms-input" placeholder="AAR / Jubilee / SHA / Britam …" />
            </div>
            <div class="col-md-6"><label class="hms-label">Card name</label>
              <input v-model="cardForm.card_name" class="hms-input" placeholder="e.g. AAR 2026 Hospital Tariff" />
            </div>
            <div class="col-md-4"><label class="hms-label">Effective from *</label>
              <input v-model="cardForm.effective_from" type="date" class="hms-input" />
            </div>
            <div class="col-md-4"><label class="hms-label">Effective to</label>
              <input v-model="cardForm.effective_to" type="date" class="hms-input" />
            </div>
            <div class="col-md-4"><label class="hms-label">Default discount %</label>
              <input v-model.number="cardForm.default_discount_pct" type="number" step="0.01" min="0" max="100" class="hms-input" placeholder="e.g. 15" />
              <div class="hms-muted-sm mt-1">Optional. Applied when a service has no specific rate.</div>
            </div>
            <div class="col-12"><label class="hms-label">Notes</label>
              <textarea v-model="cardForm.notes" rows="2" class="hms-input"></textarea>
            </div>
            <div class="col-12">
              <label class="hms-check"><input type="checkbox" v-model="cardForm.is_active" /> Active</label>
            </div>
          </div>
          <div class="d-flex justify-content-end gap-2 mt-3">
            <button class="hms-btn hms-btn-neutral" @click="showCardForm = false">Cancel</button>
            <button class="hms-btn hms-btn-primary" @click="saveCard" :disabled="saving || !cardForm.provider_name">{{ saving ? 'Saving…' : 'Save' }}</button>
          </div>
        </div>
      </div>

      <!-- Items modal -->
      <div v-if="showItems" class="hms-modal-overlay" @click.self="showItems = false">
        <div class="hms-modal" style="max-width:920px; width:95%;">
          <div class="hms-modal-title">
            <span>
              <i class="mdi mdi-format-list-bulleted me-1"></i>
              {{ itemsCard ? itemsCard.provider_name + ' — ' + (itemsCard.card_name || 'Rate card') : 'Rates' }}
            </span>
            <button class="hms-modal-close" @click="showItems = false">&times;</button>
          </div>

          <div class="d-flex justify-content-between mb-3">
            <div class="hms-muted-sm">{{ items.length }} service rates · default discount: {{ itemsCard && itemsCard.default_discount_pct !== null ? itemsCard.default_discount_pct + '%' : 'none' }}</div>
            <div class="d-flex gap-2">
              <button class="hms-btn hms-btn-blue" @click="openNewItem"><i class="mdi mdi-plus me-1"></i>Add</button>
            </div>
          </div>

          <table class="hms-table">
            <thead>
              <tr><th>Code</th><th>Service</th><th class="text-end">Catalogue</th><th class="text-end">Negotiated</th><th class="text-end">Discount %</th><th>Notes</th><th></th></tr>
            </thead>
            <tbody>
              <tr v-for="it in items" :key="it.rate_id">
                <td><code>{{ it.service_code || '—' }}</code></td>
                <td>{{ it.service_name || '—' }}</td>
                <td class="text-end">{{ it.catalogue_price != null ? Number(it.catalogue_price).toLocaleString() : '—' }}</td>
                <td class="text-end"><strong>{{ it.negotiated_price != null ? Number(it.negotiated_price).toLocaleString() : '—' }}</strong></td>
                <td class="text-end">{{ it.discount_pct != null ? it.discount_pct + '%' : '—' }}</td>
                <td class="hms-muted-sm">{{ it.notes }}</td>
                <td>
                  <button class="hms-btn-icon" @click="openEditItem(it)"><i class="mdi mdi-pencil"></i></button>
                  <button class="hms-btn-icon" @click="onDeleteItem(it)"><i class="mdi mdi-trash-can-outline"></i></button>
                </td>
              </tr>
              <tr v-if="!items.length"><td colspan="7" class="text-center hms-muted">No service overrides — the default discount applies to all services.</td></tr>
            </tbody>
          </table>

          <div class="d-flex justify-content-end gap-2 mt-3">
            <button class="hms-btn hms-btn-neutral" @click="showItems = false">Close</button>
          </div>
        </div>
      </div>

      <!-- Item form modal -->
      <div v-if="showItemForm" class="hms-modal-overlay" @click.self="showItemForm = false">
        <div class="hms-modal" style="max-width:560px; width:95%;">
          <div class="hms-modal-title">
            <span>{{ itemForm.rate_id ? 'Edit rate' : 'New rate' }}</span>
            <button class="hms-modal-close" @click="showItemForm = false">&times;</button>
          </div>
          <div class="row g-3">
            <div class="col-md-6"><label class="hms-label">Service code</label>
              <input v-model="itemForm.service_code" class="hms-input" placeholder="e.g. CONS-OPD" />
            </div>
            <div class="col-md-6"><label class="hms-label">Service name</label>
              <input v-model="itemForm.service_name" class="hms-input" placeholder="e.g. OPD Consultation" />
            </div>
            <div class="col-md-6"><label class="hms-label">Negotiated price (KES)</label>
              <input v-model.number="itemForm.negotiated_price" type="number" step="0.01" min="0" class="hms-input" placeholder="Absolute price override" />
            </div>
            <div class="col-md-6"><label class="hms-label">Discount %</label>
              <input v-model.number="itemForm.discount_pct" type="number" step="0.01" min="0" max="100" class="hms-input" placeholder="OR a percentage off catalogue" />
            </div>
            <div class="col-12"><label class="hms-label">Notes</label>
              <input v-model="itemForm.notes" class="hms-input" />
            </div>
            <div class="col-12 hms-muted-sm">
              <i class="mdi mdi-lightbulb-outline me-1"></i>Provide either an absolute <strong>negotiated price</strong> or a <strong>discount %</strong>.
              If both are empty, the card's default discount applies.
            </div>
          </div>
          <div class="d-flex justify-content-end gap-2 mt-3">
            <button class="hms-btn hms-btn-neutral" @click="showItemForm = false">Cancel</button>
            <button class="hms-btn hms-btn-primary" @click="saveItem" :disabled="saving">{{ saving ? 'Saving…' : 'Save' }}</button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { executeJson, hms } from "@/api";
import Navigation from "@/components/hms-auth/Navigation.vue";

export default {
  name: "InsuranceRateCards",
  components: { Navigation },
  data() {
    return {
      cards: [], items: [], itemsCard: null,
      showCardForm: false, showItemForm: false, showItems: false,
      saving: false,
      cardForm: this.emptyCard(),
      itemForm: this.emptyItem()
    };
  },
  mounted() {
    if (!sessionStorage.getItem("isLoggedIn")) { this.$router.push("/login"); return; }
    this.loadCards();
  },
  methods: {
    emptyCard() {
      return { card_id: null, provider_name: '', card_name: '', effective_from: new Date().toISOString().slice(0,10), effective_to: '', default_discount_pct: null, notes: '', is_active: true };
    },
    emptyItem() {
      return { rate_id: null, card_id: null, service_code: '', service_name: '', negotiated_price: null, discount_pct: null, notes: '' };
    },
    async loadCards() {
      var r = await executeJson({ function: "listInsuranceRateCards" }, hms);
      if (r.data && r.data.success) this.cards = r.data.data.cards || [];
    },
    openNewCard() { this.cardForm = this.emptyCard(); this.showCardForm = true; },
    openEditCard(c) {
      this.cardForm = {
        card_id: c.card_id,
        provider_name: c.provider_name || '',
        card_name: c.card_name || '',
        effective_from: c.effective_from || '',
        effective_to: c.effective_to || '',
        // DECIMAL comes back as a string from PHP — coerce to a Number so the input shows it.
        default_discount_pct: (c.default_discount_pct === null || c.default_discount_pct === '' || c.default_discount_pct === undefined) ? null : Number(c.default_discount_pct),
        notes: c.notes || '',
        is_active: c.is_active == 1 || c.is_active === true || c.is_active === '1'
      };
      this.showCardForm = true;
    },
    async saveCard() {
      this.saving = true;
      try {
        var r = await executeJson(Object.assign({ function: "saveInsuranceRateCard" }, this.cardForm, { is_active: this.cardForm.is_active ? 1 : 0 }), hms);
        if (r.data && r.data.success) { this.showCardForm = false; await this.loadCards(); }
      } finally { this.saving = false; }
    },
    async onDeleteCard(c) {
      if (!confirm('Delete the ' + c.provider_name + ' rate card?')) return;
      await executeJson({ function: "deleteInsuranceRateCard", card_id: c.card_id }, hms);
      await this.loadCards();
    },
    async openItems(c) {
      this.itemsCard = c;
      this.items = [];
      this.showItems = true;
      var r = await executeJson({ function: "listInsuranceRateCardItems", card_id: c.card_id }, hms);
      if (r.data && r.data.success) this.items = r.data.data.items || [];
    },
    openNewItem() {
      this.itemForm = Object.assign(this.emptyItem(), { card_id: this.itemsCard.card_id });
      this.showItemForm = true;
    },
    openEditItem(it) {
      var toNum = function(v) { return (v === null || v === undefined || v === '') ? null : Number(v); };
      this.itemForm = {
        rate_id: it.rate_id, card_id: it.card_id, service_id: it.service_id,
        service_code: it.service_code || '', service_name: it.service_name || '',
        negotiated_price: toNum(it.negotiated_price),
        discount_pct: toNum(it.discount_pct),
        notes: it.notes || ''
      };
      this.showItemForm = true;
    },
    async saveItem() {
      this.saving = true;
      try {
        var r = await executeJson(Object.assign({ function: "saveInsuranceRateCardItem" }, this.itemForm), hms);
        if (r.data && r.data.success) { this.showItemForm = false; await this.openItems(this.itemsCard); }
      } finally { this.saving = false; }
    },
    async onDeleteItem(it) {
      if (!confirm('Delete this rate?')) return;
      await executeJson({ function: "deleteInsuranceRateCardItem", rate_id: it.rate_id }, hms);
      await this.openItems(this.itemsCard);
    }
  }
};
</script>

<style scoped>
.hms-page { min-height: 100vh; background: #f7fafc; padding-top: 70px; color: #1a202c; }
.hms-page-body { padding: 16px 0 40px; }
.hms-page-header { flex-wrap: wrap; gap: 12px; }
.hms-page-title { font-size: 19px; font-weight: 700; color: #1a202c; margin: 0; }
.hms-back { color: #4a5568; font-size: 22px; text-decoration: none; display: inline-flex; align-items: center; }
.hms-back:hover { color: #2b6cb0; }

.hms-info-banner { background: #ebf8ff; border: 1px solid #90cdf4; color: #2c5282; padding: 12px 16px; border-radius: 8px; font-size: 13.5px; line-height: 1.5; }

/* Table */
.hms-table { width: 100%; border-collapse: collapse; font-size: 13.5px; background: #fff; border: 1px solid #e2e8f0; border-radius: 8px; overflow: hidden; }
.hms-table th { background: #f7fafc; padding: 10px 12px; border-bottom: 1px solid #e2e8f0; font-weight: 600; color: #4a5568; text-align: left; }
.hms-table td { padding: 10px 12px; border-bottom: 1px solid #edf2f7; vertical-align: middle; }
.hms-table tr:last-child td { border-bottom: none; }
.text-end { text-align: right; }
.text-center { text-align: center; }

/* Inputs / labels */
.hms-input { width: 100%; padding: 8px 12px; border: 1px solid #cbd5e0; border-radius: 6px; font-size: 14px; background: #fff; color: #1a202c; outline: none; transition: border-color .15s; }
.hms-input:focus { border-color: #2b6cb0; box-shadow: 0 0 0 3px rgba(43, 108, 176, 0.12); }
.hms-input-sm { padding: 5px 10px; font-size: 12.5px; }
.hms-label { font-size: 12px; font-weight: 600; color: #4a5568; display: block; margin-bottom: 4px; }
.hms-check { display: inline-flex; align-items: center; gap: 6px; font-size: 13px; color: #2d3748; }

/* Buttons */
.hms-btn { display: inline-flex; align-items: center; gap: 4px; padding: 8px 14px; border-radius: 6px; border: 1px solid transparent; font-size: 13px; font-weight: 600; cursor: pointer; transition: all .15s; }
.hms-btn:disabled { opacity: 0.55; cursor: not-allowed; }
.hms-btn-primary { background: #2b6cb0; color: #fff; }
.hms-btn-primary:hover:not(:disabled) { background: #2c5282; }
.hms-btn-blue    { background: #4299e1; color: #fff; }
.hms-btn-blue:hover:not(:disabled) { background: #3182ce; }
.hms-btn-neutral { background: #fff; color: #2d3748; border-color: #cbd5e0; }
.hms-btn-neutral:hover:not(:disabled) { background: #f7fafc; }
.hms-btn-icon { background: none; border: 1px solid transparent; color: #4a5568; padding: 5px 7px; border-radius: 5px; cursor: pointer; font-size: 16px; }
.hms-btn-icon:hover { background: #edf2f7; color: #2b6cb0; }

/* Badges */
.hms-badge { display: inline-block; padding: 3px 9px; border-radius: 10px; font-size: 11px; font-weight: 600; }
.hms-badge-success { background: #c6f6d5; color: #22543d; }
.hms-badge-info    { background: #bee3f8; color: #2a4365; }
.hms-badge-neutral { background: #edf2f7; color: #4a5568; }

/* Modal */
.hms-modal-overlay { position: fixed; inset: 0; background: rgba(0,0,0,0.55); display: flex; align-items: flex-start; justify-content: center; padding: 70px 20px 20px; overflow-y: auto; z-index: 1050; }
.hms-modal { background: #fff; border-radius: 12px; padding: 22px 24px; max-width: 720px; width: 100%; box-shadow: 0 20px 60px rgba(0,0,0,0.25); }
.hms-modal-title { font-size: 16px; font-weight: 700; color: #1a202c; display: flex; align-items: center; justify-content: space-between; margin-bottom: 16px; padding-bottom: 12px; border-bottom: 1px solid #e2e8f0; }
.hms-modal-close { background: none; border: none; font-size: 22px; cursor: pointer; color: #718096; line-height: 1; padding: 0 6px; }
.hms-modal-close:hover { color: #2d3748; }

/* Utility */
.hms-muted { color: #a0aec0; }
.hms-muted-sm { color: #a0aec0; font-size: 12px; }
.row { display: flex; flex-wrap: wrap; }
.row.g-3 { gap: 12px 0; margin: 0 -6px; }
.row.g-3 > [class*="col-"] { padding: 0 6px; }
.col-12 { flex: 0 0 100%; max-width: 100%; }
.col-md-4 { flex: 0 0 33.33%; max-width: 33.33%; }
.col-md-6 { flex: 0 0 50%; max-width: 50%; }
@media (max-width: 768px) {
  .col-md-4, .col-md-6 { flex: 0 0 100%; max-width: 100%; }
}
code { background: #edf2f7; padding: 2px 6px; border-radius: 4px; font-family: ui-monospace, Menlo, monospace; font-size: 12px; color: #2d3748; }
.py-5 { padding: 40px 0; }
.mb-3 { margin-bottom: 12px; }
.mb-4 { margin-bottom: 16px; }
.mt-1 { margin-top: 4px; }
.mt-3 { margin-top: 12px; }
.me-1 { margin-right: 4px; }
.ms-1 { margin-left: 4px; }
.gap-2 { gap: 8px; }
.gap-3 { gap: 12px; }
.d-flex { display: flex; }
.justify-content-end { justify-content: flex-end; }
.justify-content-between { justify-content: space-between; }
.align-items-end { align-items: flex-end; }
.align-items-center { align-items: center; }
</style>
